import type { InquiryListRequest } from '@ghp/api-client'
import { uuidV7Schema } from '@ghp/domain'
import { z } from 'zod'

import { urlCount, urlText } from '../../../lib/search-params'

/**
 * Le modèle de l'écran « Contrôles » : le schéma des `search params`, les deux onglets, et la
 * **couture** vers la requête serveur. Hors de la route pour que le composant d'écran reste du
 * markup.
 *
 * ⚠️ Même discipline que l'écran « Demandes » (`inquiries/lib/inquiry-list-search`) : l'URL **est**
 * l'état, chaque champ porte un `.catch` (M21 appliqué à l'URL), et **rien n'est filtré en
 * mémoire** (D24).
 */

/**
 * Les onglets de la file, et **ce que le serveur en fait** : chacun est un préréglage de filtres
 * (M14 § 1 — une vue n'est pas un concept, c'est un préréglage de filtres).
 *
 * ⚠️ **« En cours » n'est pas au prototype** (recette du 20/10/2026, M42) : les contrôles encore
 * sur la tablette dont une étape est remontée — affectés ou en cours (`load=pending`), avec une
 * étape (`progress=started`). Les deux autres sont ceux du prototype, préréglages de `status`.
 */
export const QUEUES = [
  { value: 'in_field', label: 'En cours', filter: { load: 'pending', progress: 'started' } },
  { value: 'to_validate', label: 'À valider', filter: { status: 'submitted' } },
  { value: 'validated', label: 'Validés', filter: { status: 'validated' } },
] as const satisfies readonly {
  value: string
  label: string
  filter: InquiryListRequest
}[]

export type InspectionQueue = (typeof QUEUES)[number]['value']

const QUEUE_VALUES = QUEUES.map((queue) => queue.value) as [InspectionQueue, ...InspectionQueue[]]

/**
 * Le mode d'affichage du prototype : liste à plat, ou regroupée par équipe.
 *
 * ⚠️ **Ce n'est pas de la mise en forme locale.** « Par équipe » demande au serveur de trier sur
 * l'équipe (`sort=team`) : c'est ce tri qui rend les groupes **contigus**, y compris d'une page à
 * l'autre. Grouper une page arrivée dans un autre ordre découperait la même équipe en plusieurs
 * blocs sur le même écran — un regroupement qui ne regroupe pas.
 */
export const GROUPINGS = ['team', 'flat'] as const

export type InspectionGrouping = (typeof GROUPINGS)[number]

/**
 * La troisième file, **qui n'est pas un préréglage de statut** : les contrôles libres n'ont pas de
 * demande (#419, M41), ils ont leur route (`GET /validation/free-inspections`).
 */
export const FREE_QUEUE = 'free'

export const inspectionListSearchSchema = z.object({
  queue: z
    .enum([...QUEUE_VALUES, FREE_QUEUE])
    .default('to_validate')
    .catch('to_validate'),
  // ⚠️ Le défaut est « par équipe », comme le prototype : c'est le chip actif de sa capture.
  grouping: z.enum(GROUPINGS).default('team').catch('team'),
  q: urlText(''),
  offset: urlCount(0),
  /**
   * L'équipe **part au serveur** (`teamId`), les trois suivantes non.
   *
   * ⚠️ **Le schéma du serveur, repris mot pour mot** (`teamIdSchema`,
   * `apps/api/src/inquiries/inquiry-list-query.ts`) : une valeur malformée retombe sur `'all'`,
   * un uuid v7 bien formé passe et **filtre pour de bon, même si aucune équipe ne le porte** —
   * `inquiries.repository.ts` ne teste aucune existence. Le reprendre ici fait dire à
   * `search.team` exactement ce que le serveur appliquera, donc le sélecteur peut s'y fier pour
   * s'afficher. Le déduire de l'annuaire des équipes, lui, fait annoncer « Équipe : toutes »
   * au-dessus d'une file filtrée dès que `GET /teams` échoue ou n'a pas encore répondu
   * (revue de #205).
   */
  team: z
    .union([z.literal('all'), uuidV7Schema])
    .default('all')
    .catch('all'),
  // ⚠️ Les deux filtres qui suivent restent dans l'URL parce que les listes déroulantes ont
  // besoin d'une valeur, mais **aucun ne part au serveur** — cf. `UNSERVED_FILTERS`.
  client: urlText('all'),
  lot: urlText('all'),
})

export type InspectionListSearch = z.infer<typeof inspectionListSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et « Réinitialiser ». */
export const INSPECTION_LIST_DEFAULTS: InspectionListSearch = inspectionListSearchSchema.parse({})

/**
 * Ce que chaque filtre non servi attend, nommé — pour que l'écran le dise à l'utilisateur au lieu
 * de le laisser deviner devant un contrôle inerte.
 *
 * ⚠️ Le lot a sa table et attend une condition côté API. Le client, lui, n'y est plus : cette
 * file lit la **même route** que le tableau des demandes (`api.inquiries.list`), qui filtre
 * `clientId` (M32, les deux chemins vers le donneur d'ordre).
 */
export const UNSERVED_FILTERS = {
  lot: 'le secteur existe en base (`Lot`) ; c’est la condition serveur qui manque',
} as const satisfies Partial<Record<keyof InspectionListSearch, string>>

/** Combien de lignes une page demande — même densité que le tableau des demandes. */
export const INSPECTION_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21).
 *
 * C'est ici — et nulle part ailleurs — que vit l'écart entre ce que le prototype montre et ce que
 * le modèle porte. Un filtre absent de cette fonction est un filtre que le serveur n'applique pas.
 */
export function toInspectionListQuery(search: InspectionListSearch): InquiryListRequest {
  const queue = QUEUES.find(({ value }) => value === search.queue)

  return {
    search: search.q.trim(),
    ...(queue?.filter ?? { status: 'submitted' }),
    // Le tri **est** le regroupement : voir `GROUPINGS`. À plat, on garde le tri par référence,
    // celui du tableau des demandes.
    sort: search.grouping === 'team' ? 'team' : 'reference',
    // Le paramètre d'URL s'appelle `team`, celui du serveur `teamId` : c'est un identifiant, et le
    // contrat le nomme pour ce qu'il est (M22). `'all'` est une valeur du filtre, pas une absence.
    teamId: search.team,
    clientId: search.client,
    limit: INSPECTION_PAGE_SIZE,
    offset: search.offset,
  }
}

/**
 * La même requête, réduite à **compter** : elle sert le compteur de l'onglet inactif.
 *
 * ⚠️ `limit: 1` et non la page entière. Le compteur vient de `total`, que le serveur calcule sur
 * l'ensemble filtré quelle que soit la tranche demandée (D24) : rapatrier cinquante lignes qu'on
 * n'affiche pas serait payer une page pour lire un nombre. Et surtout : ce nombre ne peut pas se
 * déduire de la page affichée, ce que la présence de ce second appel rend visible.
 *
 * ⚠️ **Prend `search` en entier, pas le seul `queue`.** Un compteur d'onglet répond à « combien si
 * je clique ici » — avec une recherche ou une équipe en cours, « ici » reste filtré de la même
 * façon. L'omettre ferait porter les deux compteurs sur deux ensembles différents : celui de
 * l'onglet actif filtré, celui de l'onglet inactif non filtré.
 */
export function toQueueCountQuery(
  queue: InspectionQueue,
  search: InspectionListSearch,
): InquiryListRequest {
  const found = QUEUES.find(({ value }) => value === queue)

  return {
    search: search.q.trim(),
    ...(found?.filter ?? { status: 'submitted' }),
    teamId: search.team,
    // Le client aussi : le compteur compte ce que l'onglet montrerait, filtres compris.
    clientId: search.client,
    limit: 1,
    offset: 0,
  }
}
